/**
 * BulkRoleAssignment - Composant d'assignation de rôles en masse
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Users, UserCheck, Shield, Search, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkRoleAssignment = ({ roles, onRefresh }) => {
  const [usersByRole, setUsersByRole] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [targetRole, setTargetRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchUsersByRole();
  }, []);

  const fetchUsersByRole = async () => {
    try {
      const response = await axios.get(`${API}/rbac/users-by-role`);
      setUsersByRole(response.data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les utilisateurs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (users) => {
    const userIds = users.map(u => u.id);
    const allSelected = userIds.every(id => selectedUsers.includes(id));
    
    if (allSelected) {
      setSelectedUsers(prev => prev.filter(id => !userIds.includes(id)));
    } else {
      setSelectedUsers(prev => [...new Set([...prev, ...userIds])]);
    }
  };

  const handleBulkAssign = async () => {
    if (!targetRole || selectedUsers.length === 0) return;

    setAssigning(true);
    try {
      const response = await axios.post(`${API}/rbac/bulk-assign`, {
        userIds: selectedUsers,
        roleSlug: targetRole
      });

      toast({
        title: "Succès",
        description: response.data.message
      });

      setSelectedUsers([]);
      setTargetRole('');
      setShowConfirmDialog(false);
      fetchUsersByRole();
      if (onRefresh) onRefresh();

    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'assignation",
        variant: "destructive"
      });
    } finally {
      setAssigning(false);
    }
  };

  // Filter users by search
  const filterUsers = (users) => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(u => 
      u.email?.toLowerCase().includes(query) ||
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(query)
    );
  };

  const targetRoleData = roles.find(r => r.slug === targetRole);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Actions Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-base px-3 py-1">
                <UserCheck className="w-4 h-4 mr-2" />
                {selectedUsers.length} sélectionné(s)
              </Badge>
            </div>

            <div className="flex-1 max-w-xs">
              <Select value={targetRole} onValueChange={setTargetRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le rôle cible..." />
                </SelectTrigger>
                <SelectContent>
                  {roles.filter(r => r.isActive).map(role => (
                    <SelectItem key={role.id} value={role.slug}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: role.color }}
                        />
                        {role.name}
                        {role.isSystem && (
                          <Badge variant="outline" className="text-xs ml-1">Système</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => setShowConfirmDialog(true)}
              disabled={selectedUsers.length === 0 || !targetRole}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Shield className="w-4 h-4 mr-2" />
              Assigner le rôle
            </Button>

            <div className="ml-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users by Role */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {usersByRole.map(group => {
          const filteredUsers = filterUsers(group.users);
          const groupSelectedCount = group.users.filter(u => selectedUsers.includes(u.id)).length;
          
          return (
            <Card key={group.role.id} className="overflow-hidden">
              <CardHeader 
                className="py-3 px-4"
                style={{ borderLeft: `4px solid ${group.role.color}` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" style={{ color: group.role.color }} />
                    <CardTitle className="text-base">{group.role.name}</CardTitle>
                    <Badge variant="outline">{group.count}</Badge>
                  </div>
                  {filteredUsers.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSelectAll(filteredUsers)}
                      className="text-xs"
                    >
                      {filteredUsers.every(u => selectedUsers.includes(u.id)) 
                        ? 'Désélectionner tout' 
                        : 'Sélectionner tout'
                      }
                    </Button>
                  )}
                </div>
                {groupSelectedCount > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 mt-1">
                    {groupSelectedCount} sélectionné(s)
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-48">
                  {filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      Aucun utilisateur
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredUsers.map(user => (
                        <label
                          key={user.id}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 ${
                            selectedUsers.includes(user.id) ? 'bg-emerald-50' : ''
                          }`}
                        >
                          <Checkbox
                            checked={selectedUsers.includes(user.id)}
                            onCheckedChange={() => handleSelectUser(user.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {user.email}
                            </div>
                          </div>
                          {!user.isActive && (
                            <Badge variant="outline" className="text-xs text-red-600">
                              Inactif
                            </Badge>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer l&apos;assignation en masse</DialogTitle>
            <DialogDescription>
              Vous êtes sur le point d&apos;assigner le rôle à {selectedUsers.length} utilisateur(s).
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${targetRoleData?.color}20` }}
                >
                  <Shield className="w-5 h-5" style={{ color: targetRoleData?.color }} />
                </div>
                <div>
                  <p className="font-medium">{targetRoleData?.name}</p>
                  <p className="text-sm text-gray-500">
                    {targetRoleData?.permissions?.length || 0} permissions
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                Cette action modifiera les permissions de {selectedUsers.length} utilisateur(s).
                Une entrée sera créée dans le journal d&apos;audit.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleBulkAssign}
              disabled={assigning}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {assigning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Assignation...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirmer l&apos;assignation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkRoleAssignment;
